import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { ConfigProvider } from 'antd';
import DashboardLayout from './components/layout/DashboardLayout';
import ProtectedRoute from './routes/ProtectedRoute';
import LoginPage from './pages/LoginPage';
import StatesOverviewPage from './pages/admin/platform/StatesOverviewPage';
import StateOnboardingPage from './pages/admin/platform/StateOnboardingPage';
import TemplatesPage from './pages/admin/platform/TemplatesPage';
import TemplateDetailPage from './pages/admin/platform/TemplateDetailPage';
import ProfilePage from './pages/admin/platform/ProfilePage';
import AddDepartmentPage from './pages/admin/platform/AddDepartmentPage';
import AddDepartmentConfigPage from './pages/admin/platform/AddDepartmentConfigPage';
import ProfileBuilderPage from './pages/admin/platform/ProfileBuilderPage';
import UnitsEditorPage from './pages/admin/platform/UnitsEditorPage';
import WorkQueuesPage from './pages/admin/platform/WorkQueuesPage';
import GovernmentPage from './pages/admin/platform/GovernmentPage';
import ReviewQueuePage from './pages/admin/platform/ReviewQueuePage';
import ImportStandardSourcesPage from './pages/admin/platform/ImportStandardSourcesPage';
import EciPullPage from './pages/admin/platform/EciPullPage';
import OrganizationsPage from './pages/admin/OrganizationsPage';
import CreateOrganizationWizard from './pages/admin/CreateOrganizationWizard';
import OrganizationViewPage from './pages/admin/OrganizationViewPage';
import UsersPage from './pages/admin/UsersPage';
import BuildHierarchyPage from './pages/admin/BuildHierarchyUnionPage';
import BuildDepartmentHierarchyPage from './pages/admin/BuildDepartmentHierarchyPage';
import DepartmentListingPage from './pages/admin/DepartmentListingPage';
import CreateGovernmentDepartmentPage from './pages/admin/CreateGovernmentDepartmentPage';
import ResourceDirectoryPage from './pages/admin/ResourceDirectoryPage';
import AddResourcePage from './pages/admin/AddResourcePage';
import ImportantPersonPage from './pages/admin/ImportantPersonPage';
import CreateUserPage from './pages/admin/CreateUserPage';
import AdminDirectoryPage from './pages/admin/AdminDirectoryPage';
import CreateAdminUserPage from './pages/admin/CreateAdminUserPage';
import TeamsPage from './pages/admin/TeamsPage';
import CreateTeamPage from './pages/admin/CreateTeamPage';
import RolesPage from './pages/admin/RolesPage';
import CreateRolePage from './pages/admin/CreateRolePage';
import CalendarPage from './pages/admin/CalendarPage';
import DataPullPage from './pages/admin/DataPullPage';
import PlaceholderPage from './pages/admin/PlaceholderPage';
import UserLayout from './components/layout/UserLayout';
import ClientLayout from './components/layout/ClientLayout';
import ClientHomePage from './pages/client/ClientHomePage';
import ClientDepartmentsPage from './pages/client/ClientDepartmentsPage';
import ClientDepartmentConfigPage from './pages/client/ClientDepartmentConfigPage';
import ClientDepartmentViewPage from './pages/client/ClientDepartmentViewPage';
import ClientPartyPage from './pages/client/ClientPartyPage';
import ClientPartyWizardPage from './pages/client/ClientPartyWizardPage';
import ClientTeamPage from './pages/client/ClientTeamPage';
import ClientGapsPage from './pages/client/ClientGapsPage';
import ClientResourcesPage from './pages/client/ClientResourcesPage';
import EngagementsPage from './pages/user/EngagementsPage';
import CreateEngagementPage from './pages/user/CreateEngagementPage';
import EngagementDetailPage from './pages/user/EngagementDetailPage';
import CreateTaskPage from './pages/user/CreateTaskPage';
import TaskDetailPage from './pages/user/TaskDetailPage';
import SubTaskDetailPage from './pages/user/SubTaskDetailPage';
import DailyDirectionsPage from './pages/user/DailyDirectionsPage';
import CreateDailyDirectionPage from './pages/user/CreateDailyDirectionPage';
import DailyDirectionStatusPage from './pages/user/DailyDirectionStatusPage';
import CitizenRequestsPage from './pages/user/CitizenRequestsPage';
import CreateCitizenRequestPage from './pages/user/CreateCitizenRequestPage';
import { fontFamily } from './theme/tokens';

function App() {
  return (
    <ConfigProvider
      theme={{
        token: {
          fontFamily,
          colorPrimary: '#0061ff',
          borderRadius: 8,
          controlHeight: 44,
          colorBorder: '#cbd5e1',
          colorText: '#00173e',
          colorTextPlaceholder: '#9ca3af',
          fontSize: 14,
        },
        components: {
          Select: { optionSelectedBg: '#eef4ff', optionSelectedColor: '#0061ff' },
          DatePicker: { activeBorderColor: '#0061ff' },
        },
      }}
    >
      <BrowserRouter>
      <Routes>
        <Route path="/login" element={<LoginPage />} />

        {/* Administrative Flow */}
        <Route
          path="/admin"
          element={
            <ProtectedRoute flow="admin">
              <DashboardLayout />
            </ProtectedRoute>
          }
        >
          {/* Platform Admin flow (new) — landing on the States overview. */}
          <Route index element={<Navigate to="overview" replace />} />
          <Route path="overview" element={<StatesOverviewPage />} />
          <Route path="onboarding" element={<StateOnboardingPage />} />
          <Route path="templates" element={<TemplatesPage />} />
          <Route path="templates/:code" element={<TemplateDetailPage />} />
          <Route path="departments" element={<ProfilePage />} />
          <Route path="departments/add" element={<AddDepartmentPage />} />
          {/* State-only hierarchy has no template to configure — open the builder directly. */}
          <Route path="departments/add/adhoc" element={<ProfileBuilderPage />} />
          <Route path="departments/add/:code" element={<AddDepartmentConfigPage />} />
          {/* People tab → open the directory create form under the department URL */}
          <Route path="departments/:code/people/political/new" element={<CreateUserPage directory="elected" />} />
          <Route path="departments/:code/people/government-official/new" element={<CreateUserPage directory="bureaucracy" />} />
          <Route path="departments/:code/people/political/:designation" element={<CreateUserPage directory="elected" />} />
          <Route path="departments/:code/people/government-official/:designation" element={<CreateUserPage directory="bureaucracy" />} />
          <Route path="departments/:code/units/:level" element={<UnitsEditorPage />} />
          <Route path="departments/:code" element={<ProfileBuilderPage />} />
          <Route path="work-queues" element={<WorkQueuesPage />} />
          <Route path="import-sources" element={<ImportStandardSourcesPage />} />
          <Route path="eci-pull" element={<EciPullPage />} />
          {/* Legacy alias (Profile → Departments) */}
          <Route path="profile" element={<Navigate to="/admin/departments" replace />} />
          <Route path="government" element={<GovernmentPage />} />
          {/* Government tab → create a person under the Government URL */}
          <Route path="government/add-minister" element={<CreateUserPage directory="elected" />} />
          <Route path="government/people/political/new" element={<CreateUserPage directory="elected" />} />
          <Route path="government/people/government-official/new" element={<CreateUserPage directory="bureaucracy" />} />
          {/* Government tab → edit an existing person in place (loads the full record) */}
          <Route path="government/people/political/:id/edit" element={<CreateUserPage directory="elected" />} />
          <Route path="government/people/government-official/:id/edit" element={<CreateUserPage directory="bureaucracy" />} />
          <Route path="review-queue" element={<ReviewQueuePage />} />

          {/* Legacy admin screens — retained by URL, no longer in the nav. */}
          <Route path="organizations" element={<OrganizationsPage />} />
          <Route path="organizations/new" element={<CreateOrganizationWizard />} />
          <Route path="organizations/new/:id" element={<CreateOrganizationWizard />} />
          <Route path="organizations/:id" element={<OrganizationViewPage />} />
          <Route path="organizations/:id/edit" element={<CreateOrganizationWizard />} />
          {/* Build Hierarchy (sidebar group) — one shared page, label per submenu */}
          <Route
            path="build-hierarchy/union"
            element={<BuildHierarchyPage key="union" label="Union Government" />}
          />
          <Route
            path="build-hierarchy/party"
            element={<BuildHierarchyPage key="party" label="Party" apiKind="org" />}
          />
          <Route
            path="build-hierarchy/state-ec"
            element={<BuildHierarchyPage key="state-ec" label="State Police" apiKind="police" />}
          />
          <Route
            path="build-hierarchy/state-government"
            element={<BuildHierarchyPage key="state-gov" label="State Revenue" apiKind="admin" />}
          />
          {/* Build Hierarchy — per-department two-step hierarchy screen (dynamic sidebar entries) */}
          <Route path="build-hierarchy/department/:id" element={<BuildDepartmentHierarchyPage />} />
          {/* Dept. Configuration — department listing + create (designation ranks) */}
          <Route path="dept-configuration" element={<DepartmentListingPage />} />
          <Route path="dept-configuration/new" element={<CreateGovernmentDepartmentPage />} />
          <Route path="dept-configuration/:id/edit" element={<CreateGovernmentDepartmentPage />} />
          {/* Resources Directory — constituency resources (schools, hospitals, …) */}
          <Route path="resource-directory" element={<ResourceDirectoryPage />} />
          <Route path="resource-directory/new" element={<AddResourcePage />} />
          <Route path="resource-directory/:id/edit" element={<AddResourcePage />} />
          <Route path="users" element={<UsersPage />} />
          {/* Directory-scoped sub-pages (sidebar → Directory group) + their dedicated create routes */}
          <Route path="users/political" element={<UsersPage lockedDirectory="elected" />} />
          <Route path="users/political/new" element={<CreateUserPage directory="elected" />} />
          <Route path="users/government-official" element={<UsersPage lockedDirectory="bureaucracy" />} />
          <Route path="users/government-official/new" element={<CreateUserPage directory="bureaucracy" />} />
          <Route path="users/party" element={<UsersPage lockedDirectory="party" />} />
          <Route path="users/party/new" element={<CreateUserPage directory="party" />} />
          <Route path="users/client-team" element={<UsersPage lockedDirectory="organization" />} />
          <Route path="users/client-team/new" element={<CreateUserPage directory="organization" />} />
          <Route path="users/others" element={<ImportantPersonPage />} />
          <Route path="users/others/new" element={<CreateUserPage directory="non_elected" />} />
          <Route path="users/others/:id/edit" element={<CreateUserPage directory="non_elected" />} />
          <Route path="users/new" element={<CreateUserPage />} />
          <Route path="users/:id/edit" element={<CreateUserPage />} />
          <Route path="directory" element={<AdminDirectoryPage />} />
          <Route path="directory/new" element={<CreateAdminUserPage />} />
          <Route path="directory/:id/edit" element={<CreateAdminUserPage />} />
          <Route path="teams" element={<TeamsPage />} />
          <Route path="teams/new" element={<CreateTeamPage />} />
          <Route path="teams/:id/edit" element={<CreateTeamPage />} />
          <Route path="roles" element={<RolesPage />} />
          <Route path="roles/new" element={<CreateRolePage />} />
          <Route path="roles/:id/edit" element={<CreateRolePage />} />
          <Route path="calendar" element={<CalendarPage />} />
          <Route path="data-pull" element={<DataPullPage />} />
        </Route>

        {/* Client Admin Flow — screens still being designed */}
        <Route
          path="/client"
          element={
            <ProtectedRoute flow="client-admin">
              <ClientLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<ClientHomePage />} />
          <Route path="departments" element={<ClientDepartmentsPage />} />
          <Route path="departments/:code/view" element={<ClientDepartmentViewPage />} />
          <Route path="departments/:code" element={<ClientDepartmentConfigPage />} />
          <Route path="party" element={<ClientPartyPage />} />
          <Route path="party/setup" element={<ClientPartyWizardPage />} />
          <Route path="team" element={<ClientTeamPage />} />
          <Route path="gaps" element={<ClientGapsPage />} />
          <Route path="resources" element={<ClientResourcesPage />} />
          {/* Resources (source data) — add / edit via the shared resource form. */}
          <Route path="resources/new" element={<AddResourcePage />} />
          <Route path="resources/:id/edit" element={<AddResourcePage />} />
          {/* Record who holds a post (from Key people & gaps) — Elected / Official. */}
          <Route path="people/political/new" element={<CreateUserPage directory="elected" />} />
          <Route path="people/government-official/new" element={<CreateUserPage directory="bureaucracy" />} />
          {/* Persons of interest (reference directory) — add / edit. */}
          <Route path="persons/new" element={<CreateUserPage directory="non_elected" />} />
          <Route path="persons/:id/edit" element={<CreateUserPage directory="non_elected" />} />
          {/* Team members (client-team directory) — add / edit. */}
          <Route path="team/new" element={<CreateUserPage directory="organization" />} />
          <Route path="team/:id/edit" element={<CreateUserPage directory="organization" />} />
        </Route>

        {/* User Flow */}
        <Route
          path="/user"
          element={
            <ProtectedRoute flow="user">
              <UserLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<Navigate to="engagements" replace />} />
          <Route path="engagements" element={<EngagementsPage />} />
          <Route path="engagements/new" element={<CreateEngagementPage />} />
          <Route path="engagements/:id/edit" element={<CreateEngagementPage />} />
          <Route path="engagements/:id" element={<EngagementDetailPage />} />
          <Route path="engagements/:id/tasks/new" element={<CreateTaskPage />} />
          <Route path="engagements/:id/tasks/:taskId/edit" element={<CreateTaskPage />} />
          <Route path="engagements/:id/tasks/:taskId" element={<TaskDetailPage />} />
          <Route
            path="engagements/:id/tasks/:taskId/subtasks/new"
            element={<CreateTaskPage variant="subtask" />}
          />
          <Route
            path="engagements/:id/tasks/:taskId/subtasks/:subId/edit"
            element={<CreateTaskPage variant="subtask" />}
          />
          <Route
            path="engagements/:id/tasks/:taskId/subtasks/:subId"
            element={<SubTaskDetailPage />}
          />
          <Route
            path="projects"
            element={<PlaceholderPage title="Projects" description="Track and manage your projects." />}
          />
          <Route path="directions" element={<DailyDirectionsPage />} />
          <Route path="directions/new" element={<CreateDailyDirectionPage />} />
          <Route path="directions/:id/edit" element={<CreateDailyDirectionPage />} />
          <Route path="directions/:id" element={<DailyDirectionStatusPage />} />
          <Route
            path="invitations"
            element={
              <PlaceholderPage title="Invitations" description="Manage event and meeting invitations." />
            }
          />
          <Route path="citizen-requests" element={<CitizenRequestsPage />} />
          <Route path="citizen-requests/new" element={<CreateCitizenRequestPage />} />
          <Route path="citizen-requests/:id/edit" element={<CreateCitizenRequestPage />} />
          <Route path="citizen-requests/wedding" element={<CitizenRequestsPage />} />
          <Route path="citizen-requests/shoksabha" element={<CitizenRequestsPage />} />
          <Route path="citizen-requests/events" element={<CitizenRequestsPage />} />
          <Route path="citizen-requests/media" element={<CitizenRequestsPage />} />
          <Route
            path="progress"
            element={
              <PlaceholderPage title="Progress Dashboard" description="Monitor campaign progress and KPIs." />
            }
          />
          <Route
            path="reports"
            element={<PlaceholderPage title="Reports" description="Generate and view field reports." />}
          />
          <Route
            path="learning"
            element={
              <PlaceholderPage title="Learning Center" description="Training material and resources." />
            }
          />
        </Route>

        <Route path="/" element={<Navigate to="/login" replace />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    </BrowserRouter>
    </ConfigProvider>
  );
}

export default App;
